import { Drawer, IconButton, Typography } from "@mui/material";
import { OLButton } from "OLButton";
import { EmptyState } from "components/EmptyState/EmptyState";
import { OLMenuButton } from "components/FormControls/OLMenuButton";
import { ReactComponent as CodeSquare02Icon } from "components/Icons/code-square-02.svg";
import { ReactComponent as CloseIcon } from "components/Icons/x-close.svg";
import { FieldArray } from "formik";
import { useMessage } from "hooks/intl";
import { TransactionItem } from "types/campaigns";
import { getEnvVariable } from "utilities/env";
import { TransactionItemFilter } from "./TransactionItemFilter/TransactionItemFilter";
import { itemFilters } from "./helpers";
import { useStyles } from "./styles";

interface Props {
  errors: any;
  isOpen: boolean;
  close: () => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => any;
  values: any;
}

const maxTransactionItemsFiltersCount = Number(
  getEnvVariable("REACT_APP_CAMPAIGN_TRANSACTION_ITEMS_FILTERS_MAX") || 2,
);

export const TransactionItemFilters = ({
  isOpen,
  close,
  errors,
  setFieldValue,
  values,
}: Props) => {
  const { message } = useMessage();
  const classes = useStyles({ isFilterContainerOpen: true });

  return (
    <Drawer
      open={isOpen}
      anchor="right"
      onClose={() => {
        setFieldValue("drafts.transactionItemsFilters", undefined, false);
        close();
      }}
      classes={{
        paper: classes.sidebar,
      }}
    >
      <div className={classes.header}>
        <Typography>{message({ id: "transactionFilters" })}</Typography>
        <IconButton onClick={close}>
          <CloseIcon />
        </IconButton>
      </div>

      <div className={classes.container}>
        <FieldArray
          name="drafts.transactionItemsFilters"
          render={({ push, form: { values, setFieldValue } }) => {
            const {
              transactionItemsFilters,
            }: { transactionItemsFilters: TransactionItem[] } = values.drafts;
            const areFiltersAdded =
              transactionItemsFilters && transactionItemsFilters.length > 0;

            return areFiltersAdded ? (
              <>
                {transactionItemsFilters.map((item, index) => {
                  const currentError =
                    errors.transactionItemsFilters &&
                    (errors.transactionItemsFilters as [])[index];
                  return (
                    <TransactionItemFilter
                      key={index}
                      transactionItemFilterIndex={index}
                      name={item.name}
                      setFieldValue={setFieldValue}
                      error={currentError}
                    />
                  );
                })}

                <OLMenuButton
                  buttonVariant="contained"
                  data-test-id="add-new-transaction-filter"
                  items={itemFilters.map((item) => {
                    return {
                      label: message({ id: item.label }),
                      onClick: () => {
                        push({
                          code: `scenario_${transactionItemsFilters.length + 1}`,
                          name: `Scenario ${transactionItemsFilters.length + 1}`,
                          filters: [
                            {
                              value: {
                                field: item.id,
                              },
                              type: item.type,
                              label: item.label,
                              isSaved: false,
                              isEditMode: false,
                            },
                          ],
                        });
                      },
                    };
                  })}
                  hideSearch
                  submit={message({
                    id: "addNewTransactionFilter",
                  })}
                  disabled={
                    transactionItemsFilters.length >=
                    maxTransactionItemsFiltersCount
                  }
                />
              </>
            ) : (
              <EmptyState
                icon={<CodeSquare02Icon />}
                title={message({ id: "addFirstTransactionItemFilters" })}
                description={message({
                  id: "campaignsTransactionFiltersDescription",
                })}
                className={classes.emptyState}
                button={
                  <OLMenuButton
                    buttonVariant="contained"
                    data-test-id="new-filter"
                    items={itemFilters.map((item, index) => {
                      return {
                        label: message({ id: item.label }),
                        onClick: () => {
                          push({
                            code: `scenario_1`,
                            name: `Scenario 1`,
                            filters: [
                              {
                                value: {
                                  field: item.id,
                                },
                                label: item.label,
                                type: item.type,
                                isSaved: false,
                                isEditMode: false,
                              },
                            ],
                          });
                        },
                      };
                    })}
                    hideSearch
                    submit={message({
                      id: "addFirstTransactionFilter",
                    })}
                  />
                }
              />
            );
          }}
        />
      </div>
      <footer className={classes.footer}>
        <OLButton
          onClick={() => {
            setFieldValue("drafts.transactionItemsFilters", undefined, false);
            close();
          }}
          variant="outlined"
        >
          {message({ id: "close" })}
        </OLButton>
        <OLButton
          variant="contained"
          onClick={() => {
            const draftValues = [...values.drafts.transactionItemsFilters];
            setFieldValue("transactionItemsFilters", draftValues);
            setFieldValue("drafts.transactionItemsFilters", undefined, false);

            close();
          }}
          data-test-id="save-transaction-filters"
        >
          {message({ id: "saveTransactionFilters" })}
        </OLButton>
      </footer>
    </Drawer>
  );
};
